import EditorJS from "@editorjs/editorjs"
import Header from "@editorjs/header"
import Underline from "@editorjs/underline"
import Paragraph from "@editorjs/paragraph"
import NestedList from "@editorjs/nested-list"
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react"
import Undo from "editorjs-undo"
import { createPortal } from "react-dom"
import { IRRSeriesChartTool } from "./IRRSeriesChartTool"
import { SmallPerformanceByStageTableTool } from "./SmallPerformanceByStageTableTool"
import { SmallPerformanceBySeriesTableTool } from "./SmallPerformanceBySeriesTableTool"
import { SmallPerformanceByAssetClassTableTool } from "./SmallPerformanceByAssetClassTableTool"
import { SmallPerformanceByVintageTableTool } from "./SmallPerformanceByVintageTableTool"
import { SmallPerformanceByLpEntityTableTool } from "./SmallPerformanceByLpEntityTableTool"
import { TopTenPortfolioCompaniesTableTool } from "./TopTenPortfolioCompaniesTableTool"
import { TopFiveSeriesTableTool } from "./TopFiveSeriesTableTool"
import { GeographicExposureTableTool } from "./GeographicExposureTableTool"
import { SectorExposureTableTool } from "./SectorExposureTableTool"
import { USMapTool } from "./USMapTool"
import { PortfolioCompanyLogosTool } from "./PortfolioCompanyLogosTool"
import { SubSectorPieChartTool } from "./SubSectorPieChartTool"
import { HistoricalPnLByVintageTableTool } from "./HistoricalPnLByVintageTableTool"
import { HistoricalPnLByAssetClassTableTool } from "./HistoricalPnLByAssetClassTableTool"
import { HistoricalIrrByAssetClassTableTool } from "./HistoricalIrrByAssetClassTableTool"
import { HistoricalIrrByVintageTableTool } from "./HistoricalIrrByVintageTableTool"
import { HistoricalPnLBySeriesTableTool } from "./HistoricalPnLBySeriesTableTool"
import { HistoricalIrrBySeriesTableTool } from "./HistoricalIrrBySeriesTableTool"
import { AttributionHighlightsTool } from "./AttributionHighlightsTool"
import { LiquidityEventsTool } from "./LiquidityEventsTool"
import { NewInvestmentsTool } from "./NewInvestmentsTool"
import { CashFlowsTool } from "./CashFlowsTool"
import { HighLevelSummaryTool } from "./HighLevelSummaryTool"

import { DataGeneratedContentTool } from "./DataGeneratedContentTool"
import { AIContentTool } from "./AIContentTool"
import { ALL_EDITOR_TOOLS } from "./editorConfig"

export type EditorHandle = {
  getContent: () => Promise<any | null>
}

type EditorProps = {
  initialData?: any
  onChange: () => void
  /**
   * Tools to enable for this editor instance.
   * Defaults to ALL_EDITOR_TOOLS (all tools enabled).
   * Only specify this if you need to restrict the available tools.
   */
  enabledTools?: readonly string[]
  chartToolData?: any
  aiContentData?: any
  reportId: string
  readOnly?: boolean
}

// The editorjs onChange callback doesn't work well with hooks because
// it's not possible to update the onChange callback dynamically
let localOnChange = () => {}
const localOnChangeCaller = () => {
  localOnChange()
}

// Tool wrappers that hide toolbox entries
function hideFromToolbox<T extends new (...args: any[]) => any>(Clazz: T) {
  return class Wrapper extends (Clazz as any) {
    static get toolbox() {
      // returning undefined prevents showing in toolbox
      return undefined
    }
  }
}

const HiddenIRRSeriesChartTool = hideFromToolbox(IRRSeriesChartTool as any)
const HiddenSmallPerformanceByStageTableTool = hideFromToolbox(
  SmallPerformanceByStageTableTool as any,
)
const HiddenSmallPerformanceBySeriesTableTool = hideFromToolbox(
  SmallPerformanceBySeriesTableTool as any,
)
const HiddenSmallPerformanceByAssetClassTableTool = hideFromToolbox(
  SmallPerformanceByAssetClassTableTool as any,
)
const HiddenSmallPerformanceByVintageTableTool = hideFromToolbox(
  SmallPerformanceByVintageTableTool as any,
)
const HiddenSmallPerformanceByLpEntityTableTool = hideFromToolbox(
  SmallPerformanceByLpEntityTableTool as any,
)
const HiddenTopTenPortfolioCompaniesTableTool = hideFromToolbox(
  TopTenPortfolioCompaniesTableTool as any,
)
const HiddenTopFiveSeriesTableTool = hideFromToolbox(
  TopFiveSeriesTableTool as any,
)
const HiddenGeographicExposureTableTool = hideFromToolbox(
  GeographicExposureTableTool as any,
)
const HiddenSectorExposureTableTool = hideFromToolbox(
  SectorExposureTableTool as any,
)
const HiddenUSMapTool = hideFromToolbox(USMapTool as any)
const HiddenPortfolioCompanyLogosTool = hideFromToolbox(
  PortfolioCompanyLogosTool as any,
)
const HiddenSubSectorPieChartTool = hideFromToolbox(
  SubSectorPieChartTool as any,
)

const HiddenHistoricalPnLByVintageTableTool = hideFromToolbox(
  HistoricalPnLByVintageTableTool as any,
)
const HiddenHistoricalPnLByAssetClassTableTool = hideFromToolbox(
  HistoricalPnLByAssetClassTableTool as any,
)

const HiddenHistoricalIrrByAssetClassTableTool = hideFromToolbox(
  HistoricalIrrByAssetClassTableTool as any,
)

const HiddenHistoricalIrrByVintageTableTool = hideFromToolbox(
  HistoricalIrrByVintageTableTool as any,
)

const HiddenHistoricalPnLBySeriesTableTool = hideFromToolbox(
  HistoricalPnLBySeriesTableTool as any,
)

const HiddenHistoricalIrrBySeriesTableTool = hideFromToolbox(
  HistoricalIrrBySeriesTableTool as any,
)

const HiddenAttributionHighlightsTool = hideFromToolbox(
  AttributionHighlightsTool as any,
)

const HiddenLiquidityEventsTool = hideFromToolbox(LiquidityEventsTool as any)

const HiddenNewInvestmentsTool = hideFromToolbox(NewInvestmentsTool as any)

const HiddenCashFlowsTool = hideFromToolbox(CashFlowsTool as any)

const HiddenHighLevelSummaryTool = hideFromToolbox(HighLevelSummaryTool as any)

export const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      reportId,
      onChange,
      initialData = {},
      enabledTools = ALL_EDITOR_TOOLS,
      chartToolData = {},
      aiContentData = {},
      readOnly = false,
    },
    ref,
  ) => {
    const editorId = useMemo(() => `editorjs-${reportId}`, [reportId])

    const [chartPortals, setChartPortals] = React.useState<React.ReactPortal[]>(
      [],
    )

    const createBlockPortal = useCallback(
      (blockId: string, node: React.ReactNode, container: HTMLElement) => {
        setChartPortals((portals) => {
          const portal = createPortal(
            node,
            container,
            `block-portal-${blockId}`,
          )
          return [...portals, portal]
        })
      },
      [],
    )
    const removeBlockPortal = useCallback(
      (blockId: string) => {
        setChartPortals((portals) => {
          return portals.filter(
            (portal) => portal.key !== `block-portal-${blockId}`,
          )
        })
      },
      [setChartPortals],
    )

    const editorRef = useRef<EditorJS | null>(null)

    useImperativeHandle(
      ref,
      () => {
        return {
          getContent: async () => {
            if (!editorRef.current) return null
            return editorRef.current?.save()
          },
        }
      },
      [],
    )

    useEffect(() => {
      return () => {
        if (editorRef.current) {
          console.log("detaching editor")
          editorRef.current.destroy?.()
        }
      }
    }, [])

    useEffect(() => {
      localOnChange = onChange
    }, [onChange])

    useEffect(() => {
      if (editorRef.current === null) {
        console.log("attaching editor")

        // Default tools available by default
        const defaultTools = {
          paragraph: {
            class: Paragraph,
            inlineToolbar: true,
            config: {
              preserveBlank: true,
            },
          },
          header: {
            class: Header,
            inlineToolbar: true,
            config: {
              levels: [1, 2, 3],
              defaultLevel: 3,
            },
          },
          list: NestedList,
        }

        const additionalTools = {
          chart: {
            class: HiddenIRRSeriesChartTool,
            config: {
              irrSeriesData: chartToolData?.irrSeriesData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          smallPerformanceByStageTable: {
            class: HiddenSmallPerformanceByStageTableTool,
            config: {
              tableData: chartToolData?.performanceTableData?.stage,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          smallPerformanceBySeriesTable: {
            class: HiddenSmallPerformanceBySeriesTableTool,
            config: {
              tableData: chartToolData?.performanceTableData?.series,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          smallPerformanceByAssetClassTable: {
            class: HiddenSmallPerformanceByAssetClassTableTool,
            config: {
              tableData: chartToolData?.performanceTableData?.assetClass,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          smallPerformanceByVintageTable: {
            class: HiddenSmallPerformanceByVintageTableTool,
            config: {
              tableData: chartToolData?.performanceTableData?.vintage,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          smallPerformanceByLpEntityTable: {
            class: HiddenSmallPerformanceByLpEntityTableTool,
            config: {
              tableData: chartToolData?.performanceTableData?.lpEntity,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          topTenPortfolioCompaniesTable: {
            class: HiddenTopTenPortfolioCompaniesTableTool,
            config: {
              tableData: chartToolData?.topTenPortfolioCompaniesData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          topFiveSeriesTable: {
            class: HiddenTopFiveSeriesTableTool,
            config: {
              tableData: chartToolData?.topFiveSeriesData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          geographicExposureTable: {
            class: HiddenGeographicExposureTableTool,
            config: {
              tableData: chartToolData?.geographicExposureData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          sectorExposureTable: {
            class: HiddenSectorExposureTableTool,
            config: {
              tableData: chartToolData?.sectorExposureData,
              sp500DataAsOfDate: chartToolData?.sp500DataAsOfDate,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          usMap: {
            class: HiddenUSMapTool,
            config: {
              mapData: chartToolData?.geographicExposureData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          portfolioCompanyLogos: {
            class: HiddenPortfolioCompanyLogosTool,
            config: {
              portfolioCompanies: chartToolData?.portfolioCompanies,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          subSectorPieChart: {
            class: HiddenSubSectorPieChartTool,
            config: {
              subSectorData: chartToolData?.subSectorExposureData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalPnLByVintageTable: {
            class: HiddenHistoricalPnLByVintageTableTool,
            config: {
              tableData: chartToolData?.historicalPnLByVintageData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalPnLByAssetClassTable: {
            class: HiddenHistoricalPnLByAssetClassTableTool,
            config: {
              tableData: chartToolData?.historicalPnLByAssetClassData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalIrrByAssetClassTable: {
            class: HiddenHistoricalIrrByAssetClassTableTool,
            config: {
              tableData: chartToolData?.historicalIrrByAssetClassData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalIrrByVintageTable: {
            class: HiddenHistoricalIrrByVintageTableTool,
            config: {
              tableData: chartToolData?.historicalIrrByVintageData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalPnLBySeriesTable: {
            class: HiddenHistoricalPnLBySeriesTableTool,
            config: {
              tableData: chartToolData?.historicalPnLBySeriesData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          historicalIrrBySeriesTable: {
            class: HiddenHistoricalIrrBySeriesTableTool,
            config: {
              tableData: chartToolData?.historicalIrrBySeriesData,
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
            },
          },
          attributionHighlights: {
            class: HiddenAttributionHighlightsTool,
            config: {
              highlights: aiContentData?.attributionHighlights || [],
              style: "unordered",
            },
          },
          liquidityEvents: {
            class: HiddenLiquidityEventsTool,
            config: {
              liquidityEvents: aiContentData?.liquidityEvents || [],
              style: "unordered",
            },
          },
          newInvestments: {
            class: HiddenNewInvestmentsTool,
            config: {
              newInvestments: aiContentData?.newInvestments || [],
              style: "unordered",
            },
          },
          cashFlows: {
            class: HiddenCashFlowsTool,
            config: {
              cashFlows:
                aiContentData?.cashFlows ||
                "No significant cash flow activity this quarter",
            },
          },
          highLevelSummary: {
            class: HiddenHighLevelSummaryTool,
            config: {
              highLevelSummary:
                aiContentData?.highLevelSummary ||
                "No high level summary available for this quarter",
            },
          },
          aiContent: {
            class: AIContentTool,
            config: {
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
              insertables: {
                attributionHighlights: {
                  toolName: "attributionHighlights",
                  label: "Attribution highlights",
                },
                liquidityEvents: {
                  toolName: "liquidityEvents",
                  label: "Liquidity Events",
                },
                newInvestments: {
                  toolName: "newInvestments",
                  label: "New Investments",
                },
                cashFlows: {
                  toolName: "cashFlows",
                  label: "Cash Flows",
                },
                highLevelSummary: {
                  toolName: "highLevelSummary",
                  label: "High Level Summary",
                },
              },
            },
          },
          dataGeneratedContent: {
            class: DataGeneratedContentTool,
            config: {
              createPortal: createBlockPortal,
              removePortal: removeBlockPortal,
              insertables: {
                irrSeriesChart: {
                  toolName: "chart",
                  label: "IRRSeriesChartTool",
                },
                topTenPortfolioCompaniesTable: {
                  toolName: "topTenPortfolioCompaniesTable",
                  label: "Top 10 Portfolio Companies",
                },
                topFiveSeriesTable: {
                  toolName: "topFiveSeriesTable",
                  label: "Top 5 Series",
                },
                geographicExposureTable: {
                  toolName: "geographicExposureTable",
                  label: "Geographic Exposure",
                },
                sectorExposureTable: {
                  toolName: "sectorExposureTable",
                  label: "Sector Exposure",
                },
                usMap: {
                  toolName: "usMap",
                  label: "Geographic Exposure Map",
                },
                portfolioCompanyLogos: {
                  toolName: "portfolioCompanyLogos",
                  label: "Portfolio Company Logos",
                },
                subSectorPieChart: {
                  toolName: "subSectorPieChart",
                  label: "Sub-Sector Pie Chart",
                },
                historicalPnLByVintageTable: {
                  toolName: "historicalPnLByVintageTable",
                  label: "Historical P&L by Vintage",
                },
                historicalPnLByAssetClassTable: {
                  toolName: "historicalPnLByAssetClassTable",
                  label: "Historical P&L by Asset Class",
                },
                historicalIrrByAssetClassTable: {
                  toolName: "historicalIrrByAssetClassTable",
                  label: "Historical IRR by Asset Class",
                },
                historicalIrrByVintageTable: {
                  toolName: "historicalIrrByVintageTable",
                  label: "Historical IRR by Vintage",
                },
                historicalPnLBySeriesTable: {
                  toolName: "historicalPnLBySeriesTable",
                  label: "Historical P&L by Series",
                },
                historicalIrrBySeriesTable: {
                  toolName: "historicalIrrBySeriesTable",
                  label: "Historical IRR by Series",
                },
                performanceTables: {
                  label: "Performance tables",
                  bySeries: {
                    toolName: "smallPerformanceBySeriesTable",
                    label: "by series",
                  },
                  byStage: {
                    toolName: "smallPerformanceByStageTable",
                    label: "by stage",
                  },
                  byAssetClass: {
                    toolName: "smallPerformanceByAssetClassTable",
                    label: "by asset class",
                  },
                  byVintage: {
                    toolName: "smallPerformanceByVintageTable",
                    label: "by vintage",
                  },
                  byLpEntity: {
                    toolName: "smallPerformanceByLpEntityTable",
                    label: "by lp entity",
                  },
                },
              },
            },
          },
        }

        const activeTools: Record<string, any> = {
          ...defaultTools,
          ...Object.fromEntries(
            Object.entries(additionalTools)
              .filter(([key]) => enabledTools.includes(key))
              .filter(([key, tool]) => {
                // In read-only mode, filter out tools that don't support it
                if (
                  readOnly &&
                  tool.class &&
                  tool.class.isReadOnlySupported === false
                ) {
                  return false
                }
                return true
              }),
          ),
        }

        const registeredToolKeys = Object.keys(activeTools)
        if (activeTools.dataGeneratedContent?.config) {
          activeTools.dataGeneratedContent.config.registeredToolKeys =
            registeredToolKeys
        }

        if (activeTools.aiContent?.config) {
          activeTools.aiContent.config.registeredToolKeys = registeredToolKeys
        }

        editorRef.current = new EditorJS({
          holder: editorId,
          autofocus: !readOnly,
          data: initialData,
          placeholder: readOnly ? "" : "Content here...",
          hideToolbar: readOnly,
          inlineToolbar: !readOnly,
          onChange: readOnly ? () => {} : localOnChangeCaller,
          onReady: () => {
            if (!readOnly) {
              const undo = new Undo({
                editor: editorRef.current,
                config: {
                  shortcuts: {
                    undo: "CMD+Z",
                    redo: "CMD+SHIFT+Z",
                  },
                },
              })
              undo.initialize(initialData)
            }
          },
          defaultBlock: "paragraph",
          tools: activeTools as any,
          readOnly: readOnly,
        })
      }
    }, [editorId, initialData, onChange])

    return (
      <>
        {chartPortals.map((portal) => (
          <React.Fragment key={portal.key}>{portal}</React.Fragment>
        ))}

        <div className="editorjs" id={editorId} />
      </>
    )
  },
)
